export type GnEventType =
  | 'gn.mount'
  | 'gn.unmount'
  | 'gn.render'
  | 'gn.api_fetch'
  | 'gn.chunk_load_error'
  | 'gn.render_error'
  | 'gn.version_load';

type AwsRumBootstrap = {
  n?: string;
};

declare global {
  interface Window {
    AwsRumClient?: AwsRumBootstrap;
    cwr?: (command: string, payload?: unknown) => void;
  }
}

const BUFFER_CAP = 50;
const POLL_INTERVAL_MS = 2_000;
const TIMEOUT_MS = 30_000;
const DEFAULT_CWR_COMMAND = 'cwr';

let buffer: Array<{ eventType: GnEventType; data: Record<string, unknown> }> = [];
let pollingInterval: ReturnType<typeof setInterval> | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

function getCwrCommand(): ((command: string, payload?: unknown) => void) | null {
  try {
    const commandName = window.AwsRumClient?.n ?? DEFAULT_CWR_COMMAND;
    const command = (window as unknown as Record<string, unknown>)[commandName];
    if (typeof command === 'function') {
      return command as (command: string, payload?: unknown) => void;
    }
  } catch {
    // no window or no client
  }
  return null;
}

function sendCustomEvent(eventType: string, data: Record<string, unknown>): void {
  const cwr = getCwrCommand();
  if (!cwr) return;
  cwr('recordEvent', { type: eventType, data });
}

function flushBuffer(): void {
  const pending = buffer;
  buffer = [];
  for (const entry of pending) {
    try {
      sendCustomEvent(entry.eventType, entry.data);
    } catch {
      // swallow per-event errors
    }
  }
}

function stopPolling(): void {
  if (pollingInterval !== null) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
}

function startPolling(): void {
  if (pollingInterval !== null) return;

  pollingInterval = setInterval(() => {
    if (getCwrCommand()) {
      stopPolling();
      flushBuffer();
    }
  }, POLL_INTERVAL_MS);

  timeoutId = setTimeout(() => {
    stopPolling();
    buffer = [];
  }, TIMEOUT_MS);
}

export function recordGnEvent(eventType: GnEventType, data: Record<string, unknown>): void {
  const stamped = { ...data, timestamp: Date.now() };

  if (getCwrCommand()) {
    try {
      sendCustomEvent(eventType, stamped);
    } catch {
      // swallow
    }
    return;
  }

  if (buffer.length < BUFFER_CAP) {
    buffer.push({ eventType, data: stamped });
  }
  startPolling();
}

export function _resetForTesting(): void {
  stopPolling();
  buffer = [];
}
